import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Loader2, Lock, RefreshCw, FlaskConical, GitBranch, DollarSign,
  Sparkles, Gauge, ChevronRight, ShieldAlert, Search,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "calibration", label: "Calibration", icon: Gauge },
  { id: "valueadd", label: "Claude Value-Add", icon: Sparkles },
  { id: "baselines", label: "Baselines", icon: FlaskConical },
  { id: "telemetry", label: "Cost / Ops", icon: DollarSign },
  { id: "lifecycle", label: "Lifecycle", icon: GitBranch },
];

const SAMPLE_CLS = {
  "INSUFFICIENT SAMPLE": "text-tc-bear border-tc-bear/40",
  "EARLY SIGNAL": "text-tc-mastery border-tc-mastery/40",
  "EMERGING PATTERN": "text-tc-xp border-tc-xp/40",
  "SUPPORTED PATTERN": "text-tc-coach border-tc-coach/40",
};

function Pill({ children, cls = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-mono ${cls}`}>{children}</span>;
}
function Sample({ label, n }) {
  return <Pill cls={SAMPLE_CLS[label] || "text-tc-muted border-white/15"}>{label}{n != null ? ` · N=${n}` : ""}</Pill>;
}
function Card({ title, right, children, testid }) {
  return (
    <div className="tc-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-tc-txt">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-white/5 last:border-0">
      <span className="text-tc-sub text-sm">{k}</span>
      <span className="text-tc-txt font-mono text-sm">{v == null ? "—" : v}</span>
    </div>
  );
}
function Dist({ obj }) {
  const entries = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <div className="text-tc-muted text-sm">No data yet.</div>;
  const max = Math.max(...entries.map((e) => e[1]));
  return (
    <div className="space-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <div className="w-40 shrink-0 text-tc-sub text-xs truncate">{k || "—"}</div>
          <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-tc-coach/70 rounded-full" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <div className="w-8 text-right font-mono text-xs text-tc-txt">{v}</div>
        </div>
      ))}
    </div>
  );
}

export default function Quality() {
  const navigate = useNavigate();
  const [authz, setAuthz] = useState(null);
  const [tab, setTab] = useState("overview");
  const [scope] = useState("shadow");
  const [horizon, setHorizon] = useState("medium");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState({});
  const [cid, setCid] = useState("");
  const [life, setLife] = useState(null);

  useEffect(() => {
    api.get("/quality/authorization").then((r) => setAuthz(r.data)).catch(() => setAuthz({ authorized: false }));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const q = `?scope=${scope}&horizon=${horizon}`;
      const [dash, cal, va, base, tel] = await Promise.all([
        api.get(`/quality/dashboard${q}`), api.get(`/quality/calibration${q}`),
        api.get(`/quality/valueadd${q}`), api.get(`/quality/baselines${q}`), api.get(`/quality/telemetry${q}`),
      ]);
      setData({ dash: dash.data, cal: cal.data, va: va.data, base: base.data, tel: tel.data });
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  };

  useEffect(() => { if (authz?.authorized) load(); /* eslint-disable-next-line */ }, [authz, horizon]);

  const runEval = async () => {
    setRunning(true);
    try { const { data: r } = await api.post(`/quality/evaluate/run?scope=${scope}`); toast.success(`Evaluator: ${r.evaluations_written} written, ${r.backlog} awaiting horizon`); await load(); }
    catch (e) { toast.error(apiErr(e)); }
    setRunning(false);
  };

  const lookupLife = async () => {
    if (!cid.trim()) return;
    try { const { data: r } = await api.get(`/quality/lifecycle/${cid.trim()}`); setLife(r); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (authz && !authz.authorized) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="tc-card p-8 text-center border-tc-mastery/25" data-testid="quality-gated">
          <Lock className="w-8 h-8 mx-auto text-tc-mastery" />
          <h1 className="font-heading text-2xl font-bold mt-3">Intelligence Quality is an internal instrument</h1>
          <p className="text-tc-sub mt-2">This measurement surface is available to adult / admin accounts only.</p>
          <button data-testid="quality-back-btn" onClick={() => navigate("/intelligence")} className="mt-5 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">Back to Intelligence</button>
        </div>
      </div>
    );
  }

  const d = data.dash, cal = data.cal, va = data.va, base = data.base, tel = data.tel;

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="quality-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-tc-coach" /> Intelligence Quality
          </h1>
          <p className="text-tc-sub text-sm mt-1 max-w-2xl">
            Internal forward-evaluation instrument. Measures how good the intelligence is using information frozen BEFORE outcomes occurred. Not a marketing scoreboard; every metric shows its sample size and evidence class.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Pill cls="text-tc-coach border-tc-coach/40">EVIDENCE: {d?.evidence_class || "REAL_FORWARD"}</Pill>
          <select data-testid="quality-horizon" value={horizon} onChange={(e) => setHorizon(e.target.value)}
            className="bg-tc-bg border border-white/15 rounded-lg px-3 py-2 text-sm text-tc-txt">
            <option value="short">Short (2h)</option>
            <option value="medium">Medium (8h)</option>
            <option value="extended">Extended (24h)</option>
          </select>
          <button data-testid="quality-run-eval" onClick={runEval} disabled={running}
            className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run Evaluation
          </button>
        </div>
      </div>

      <div className="tc-card p-3 my-4 border-tc-mastery/20 flex items-start gap-2" data-testid="quality-antioverfit">
        <ShieldAlert className="w-4 h-4 text-tc-mastery mt-0.5 shrink-0" />
        <p className="text-tc-sub text-xs">Sprint 9 is a measurement layer. No production thresholds, weights, taxonomy or prompts are tuned from these results. Findings are hypotheses, not established truths.</p>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-5">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`quality-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors border ${tab === t.id ? "bg-tc-coach/15 text-tc-coach border-tc-coach/30" : "text-tc-sub hover:text-tc-txt border-transparent hover:bg-white/5"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-tc-sub py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading measurements…</div>
      ) : (
        <>
          {tab === "overview" && d && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="quality-overview">
              <Card title="Scan funnel">
                <KV k="Total scans" v={d.scans.total} />
                <KV k="Markets evaluated" v={d.scans.markets_evaluated} />
                <KV k="Candidates surfaced" v={d.scans.candidates_surfaced} />
                <KV k="Filtered out" v={d.scans.filtered_out_pct != null ? `${d.scans.filtered_out_pct}%` : "—"} />
                <KV k="Data-quality rejections" v={`${d.data_quality.rejections} (${d.data_quality.stale_rejections} stale)`} />
              </Card>
              <Card title="Candidate mix" right={<Sample label={d.candidates.sample} n={d.candidates.surfaced} />}>
                <div className="text-[11px] uppercase tracking-wide text-tc-muted mb-1">By situation</div>
                <Dist obj={d.candidates.by_situation} />
                <div className="text-[11px] uppercase tracking-wide text-tc-muted mt-3 mb-1">By actionability · WAIT {d.candidates.wait_count} · PASS/no-edge {d.candidates.pass_no_edge_count}</div>
                <Dist obj={d.candidates.by_actionability} />
              </Card>
              <Card title="Attention Priority distribution">
                <Dist obj={d.candidates.attention_priority_distribution} />
                <p className="text-tc-muted text-[11px] mt-2">Attention ranking construct — not a win probability or expected return.</p>
              </Card>
              <Card title={`Forward outcomes · ${horizon}`}>
                {Object.entries(d.forward_evaluation.by_horizon).map(([h, g]) => (
                  <div key={h} className="mb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-tc-sub text-sm capitalize">{h}</span><Sample label={g.sample} n={g.n} />
                    </div>
                    {g.n > 0 && (
                      <div className="text-xs font-mono text-tc-muted mt-1">
                        trig {g.triggered_rate ?? "—"} · inval {g.invalidated_rate ?? "—"} · unresolved {g.unresolved_rate ?? "—"} · reasoning {g.mean_reasoning_quality ?? "—"}
                      </div>
                    )}
                  </div>
                ))}
              </Card>
            </div>
          )}

          {tab === "calibration" && cal && (
            <div className="space-y-4" data-testid="quality-calibration">
              <Card title={`Calibration · ${cal.horizon}`} right={<Sample label={cal.sample} n={cal.n_total} />}>
                {cal.findings?.length ? cal.findings.map((f, i) => (
                  <div key={i} className="text-tc-sub text-sm py-1 flex gap-2"><ChevronRight className="w-4 h-4 text-tc-mastery mt-0.5" />{f}</div>
                )) : <div className="text-tc-muted text-sm">No calibration findings yet — accumulating forward data.</div>}
                <p className="text-tc-muted text-[11px] mt-2">Methodology {cal.methodology.version}. Exploratory only; no production tuning.</p>
              </Card>
              {[["By Attention band", cal.by_attention_band], ["By actionability", cal.by_actionability], ["By situation", cal.by_situation], ["By interpreted", cal.by_interpreted]].map(([label, grp]) => (
                <Card key={label} title={label}>
                  {Object.keys(grp || {}).length ? (
                    <div className="space-y-1.5">
                      {Object.entries(grp).map(([k, g]) => (
                        <div key={k} className="flex flex-wrap items-center justify-between gap-2 py-1 border-b border-white/5 last:border-0">
                          <span className="text-tc-txt text-sm font-mono">{k}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-tc-muted">trig1st {g.triggered_first_rate ?? "—"} · reason {g.mean_reasoning_quality ?? "—"} · calib {g.mean_calibration ?? "—"}</span>
                            <Sample label={g.sample} n={g.n} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : <div className="text-tc-muted text-sm">No evaluated samples yet.</div>}
                </Card>
              ))}
            </div>
          )}

          {tab === "valueadd" && va && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="quality-valueadd">
              <Card title="Claude value-add classification" right={<Sample label={va.sample} n={va.n} />}>
                <Dist obj={va.distribution} />
                <p className="text-tc-sub text-sm mt-3">{va.note}</p>
                <p className="text-tc-muted text-[11px] mt-2">Deterministic classifier ({va.version}). Claude never grades itself; a human-review slot is reserved.</p>
              </Card>
              <Card title="What this measures">
                <ul className="text-tc-sub text-sm space-y-1.5 list-disc pl-4">
                  <li><b className="text-tc-txt">VALUE_ADDED</b> — surfaced conflicts / alternatives / ambiguity beyond the deterministic read.</li>
                  <li><b className="text-tc-txt">USEFUL_REINFORCEMENT</b> — appropriately reinforced restraint.</li>
                  <li><b className="text-tc-txt">REDUNDANT</b> — restated the deterministic read.</li>
                  <li><b className="text-tc-txt">MIXED / DEGRADED</b> — added value but/or introduced unsupported claims.</li>
                  <li><b className="text-tc-txt">INSUFFICIENT</b> — no interpretation to judge.</li>
                </ul>
              </Card>
            </div>
          )}

          {tab === "baselines" && base && (
            <Card title={`Baselines · ${base.horizon}`} right={<Sample label={base.sample} n={base.n} />} testid="quality-baselines">
              <p className="text-tc-sub text-sm mb-3">{base.note}</p>
              <div className="space-y-2">
                {Object.entries(base.methods).map(([k, m]) => (
                  <div key={k} className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0">
                    <div>
                      <div className="text-tc-txt text-sm font-mono">{k}</div>
                      <div className="text-tc-muted text-xs">{m.desc}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-sm text-tc-txt">{m.meaningful_rate ?? m.expected_meaningful_rate ?? (m.status || "—")}</div>
                      {m.k != null && <div className="text-[10px] text-tc-muted">top-K={m.k}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-tc-muted text-[11px] mt-3">{base.meaningful_definition}</p>
            </Card>
          )}

          {tab === "telemetry" && tel && (
            <div className="grid md:grid-cols-3 gap-4" data-testid="quality-telemetry">
              <Card title="Scans"><KV k="Scans" v={tel.n_scans} /><KV k="Symbols screened" v={tel.scan.symbols_screened} /><KV k="Surfaced" v={tel.scan.symbols_surfaced} /><KV k="Avg latency" v={tel.scan.avg_scan_latency_ms != null ? `${tel.scan.avg_scan_latency_ms} ms` : "—"} /><KV k="Provider errors" v={tel.scan.provider_errors} /></Card>
              <Card title="Claude (selective)"><KV k="Calls" v={tel.claude.calls} /><KV k="Cache hits" v={tel.claude.cache_hits} /><KV k="Skipped unchanged" v={tel.claude.skipped_unchanged} /><KV k="Skipped (cap)" v={tel.claude.skipped_capped} /><KV k="Interpretations" v={tel.claude.interpretations_stored} /><KV k="Est. cost" v={`$${tel.claude.estimated_cost_usd}`} /></Card>
              <Card title="Evaluator + unit cost"><KV k="Runs" v={tel.evaluator.runs} /><KV k="Evaluations" v={tel.evaluator.evaluations_total} /><KV k="Value-add written" v={tel.evaluator.valueadd_written} /><KV k="Backlog" v={tel.evaluator.backlog} /><KV k="Per scan" v={tel.unit_costs.per_scan_usd != null ? `$${tel.unit_costs.per_scan_usd}` : "—"} /><KV k="Per Claude call" v={tel.unit_costs.per_claude_call_usd != null ? `$${tel.unit_costs.per_claude_call_usd}` : "—"} /></Card>
            </div>
          )}

          {tab === "lifecycle" && (
            <div className="space-y-4" data-testid="quality-lifecycle">
              <Card title="Candidate forensic timeline">
                <div className="flex gap-2">
                  <input data-testid="quality-cid-input" value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Paste a candidate_id"
                    className="flex-1 bg-tc-bg border border-white/15 rounded-lg px-3 py-2 text-sm text-tc-txt" />
                  <button data-testid="quality-cid-lookup" onClick={lookupLife} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-4 py-2 text-sm flex items-center gap-2"><Search className="w-4 h-4" /> Trace</button>
                </div>
                <p className="text-tc-muted text-[11px] mt-2">Find a candidate_id in Intelligence → open candidate, or in the /quality/evaluations feed.</p>
              </Card>
              {life && (
                <Card title={`${life.symbol} · ${life.snapshots.length} snapshot(s)`}>
                  <p className="text-tc-muted text-[11px] mb-3">{life.note}</p>
                  <div className="space-y-3">
                    {life.snapshots.map((s, i) => (
                      <div key={s.candidate_id} className="border-l-2 border-tc-coach/40 pl-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-tc-coach">T{i}</span>
                          <span className="text-sm text-tc-txt">{s.situation_type} · {s.actionability} · {s.lean}</span>
                          {s.tier && <Pill cls="text-tc-sub border-white/15">{s.tier}</Pill>}
                          <Pill cls="text-tc-muted border-white/15">AP {s.attention_priority?.total}</Pill>
                          <Pill cls="text-tc-muted border-white/15">{s.condition_maturity?.state}</Pill>
                        </div>
                        {s.what_changed?.length ? <div className="text-xs text-tc-sub mt-1">Changed: {s.what_changed.map((c) => c.text).join("; ")}</div> : null}
                        {s.interpretation?.available && <div className="text-xs text-tc-mastery mt-1">Claude: {s.interpretation.synthesis}</div>}
                        {s.claude_value_add && <div className="text-[11px] text-tc-muted mt-0.5">Value-add: {s.claude_value_add.classification}</div>}
                        {s.forward_evaluations?.length ? (
                          <div className="text-[11px] font-mono text-tc-muted mt-1">
                            {s.forward_evaluations.map((e) => `${e.horizon}:${e.outcome?.resolution || "n/a"}`).join(" · ")}
                          </div>
                        ) : <div className="text-[11px] text-tc-muted mt-1">Forward evaluation pending (horizon not elapsed).</div>}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
